// src/components/MyProfileModal.jsx
//
// Self-service phone number. Every user needed a way to set their own —
// PUT /api/users/:id (used by the admin-facing Users screen) is gated to
// admins/managers only, so a plain sales user had no way to touch their own
// record at all. PUT /api/auth/me is the deliberately narrow counterpart:
// open to anyone, and only ever writes the caller's own phone.
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Alert, CircularProgress
} from '@mui/material';
import api from '../utils/api';

const MyProfileModal = ({ open, onClose, onSaved }) => {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetched fresh rather than trusting the cached login-time user object,
  // which may predate this field and never gets refreshed until next login.
  useEffect(() => {
    if (!open) return;
    setError(''); setSuccess('');
    setFetching(true);
    api.get('/auth/me')
      .then(res => setPhone(res.data?.phone || ''))
      .catch(() => setError('Could not load your current profile'))
      .finally(() => setFetching(false));
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(''); setSuccess('');
    try {
      const res = await api.put('/auth/me', { phone: phone.trim() || null });
      setSuccess('Profile updated');
      onSaved?.(res.data);
      setTimeout(() => { onClose(); setSuccess(''); }, 1200);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose();
      }}
      maxWidth="sm" fullWidth
    >
      <DialogTitle>My Profile</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <TextField
            label="Phone Number"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            fullWidth
            disabled={fetching}
            placeholder="e.g. 9876543210"
            helperText="Shown on quotations you prepare, next to your name and email."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            type="submit" variant="contained" disabled={loading || fetching}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MyProfileModal;
